import React, { useState, Children, cloneElement, useCallback, forwardRef } from 'react';
import { StyledTabsList } from './styled';
/**
 * Type definition for Tabs component props.
 *
 * @typedef {Object} TabsPropsT
 * @property {number} [activeIndex] - The index of the currently active tab.
 * @property {ReactElement<TabPropsT>[]} children - The tab elements to be rendered inside the Tabs component.
 * @property {'start' | 'center' | 'flex-end'} [justify='center'] - The justification of the tabs within the tab list.
 * @property {'start' | 'center' | 'flex-end'} [align='center'] - The alignment of the tabs within the tab list.
 * @property {function(number):void} [onActive] - Callback function called when a tab becomes active.
 * @property {string} [className] - Additional class name for styling the component.
 */
export const Tabs = forwardRef(function Tabs({ children, activeIndex: activeIndexPassed, justify = 'center', align = 'center', onActive, ...rest }, ref) {
    const [activeIndex, setActiveIndex] = useState(() => activeIndexPassed || 0);
    const handleActivateTab = useCallback((index) => () => {
        if (onActive) {
            onActive(index);
        }
        setActiveIndex(index);
    }, [onActive, setActiveIndex]);
    const tabs = Children.map(children, (child, index) => {
        return cloneElement(child, {
            active: activeIndex === index,
            onActivate: handleActivateTab(index)
        });
    });
    return (React.createElement(StyledTabsList, { role: "tablist", justify: justify, align: align, ref: ref, ...rest }, tabs));
});
